import React, {useMemo} from 'react';
import {
	AbsoluteFill,
	useCurrentFrame,
	useVideoConfig,
	interpolate,
	spring,
	Easing,
} from 'remotion';

/* ─── Types ───────────────────────────────────────────────────────── */

interface SubtitleSegment {
	text: string;
	highlight?: boolean;
	/** When true, highlight uses slam + lens flare; otherwise gentle entrance */
	impact?: boolean;
}

interface SubtitleLine {
	segments: SubtitleSegment[];
	startFrame: number;
}

export interface BoldStrokeSubtitleProps {
	lines: SubtitleLine[];
	fontSize?: number;
	highlightFontSize?: number;
	strokeWidth?: number;
	highlightColor?: string;
	normalStrokeColor?: string;
	highlightStrokeColor?: string;
	segmentInterval?: number;
	position?: 'bottom' | 'center';
	skewDeg?: number;
	/** 'centered' – all segments center-aligned (default);
	 *  'staggered' – normal segments left, highlight segments right */
	layout?: 'centered' | 'staggered';
}

/* ─── Defaults ────────────────────────────────────────────────────── */

const DEFAULT_PROPS: BoldStrokeSubtitleProps = {
	lines: [
		{
			startFrame: 0,
			segments: [
				{text: '吃到热腾腾饭菜'},
				{text: '幸福感', highlight: true, impact: true},
			],
		},
		{
			startFrame: 60,
			segments: [
				{text: '是花20块钱就能获得'},
				{text: '妈妈味道的满足', highlight: true},
			],
		},
		{
			startFrame: 120,
			segments: [
				{text: '我想说做猪脚饭'},
				{text: '并非我的本意', highlight: true, impact: true},
			],
		},
		{
			startFrame: 180,
			segments: [
				{text: '他卖的是'},
				{text: '三餐跟四季', highlight: true, impact: true},
			],
		},
		{
			startFrame: 240,
			segments: [
				{text: '董宇辉说卖大米'},
				{text: '并非他的本意', highlight: true},
			],
		},
	],
	fontSize: 72,
	highlightFontSize: 96,
	strokeWidth: 10,
	highlightColor: '#FFD700',
	normalStrokeColor: '#252a3a',
	highlightStrokeColor: '#7c1a1a',
	segmentInterval: 15,
	position: 'bottom',
	skewDeg: -5,
	layout: 'centered',
};

const FONT =
	'"PingFang SC","Noto Sans SC","Microsoft YaHei","Heiti SC",sans-serif';

/* ─── Stroke rendering ────────────────────────────────────────────── */

function buildStrokeStyle(
	color: string,
	strokePx: number,
	isHighlight: boolean,
): {webkitStroke: string; textShadow: string} {
	const ring: string[] = [];
	const outerR = Math.ceil(strokePx * 0.6);
	for (let i = 0; i < 16; i++) {
		const a = (i / 16) * Math.PI * 2;
		const x = Math.round(Math.cos(a) * outerR);
		const y = Math.round(Math.sin(a) * outerR);
		ring.push(`${x}px ${y}px 0 ${color}`);
	}

	const depth = isHighlight
		? [
				`3px 3px 0 ${color}`,
				`5px 5px 0 ${color}`,
				`6px 7px 0 rgba(0,0,0,0.45)`,
				`7px 9px 6px rgba(0,0,0,0.35)`,
			]
		: [`2px 3px 0 ${color}`, `3px 5px 3px rgba(0,0,0,0.3)`];

	return {
		webkitStroke: `${strokePx}px ${color}`,
		textShadow: [...ring, ...depth].join(', '),
	};
}

/* ─── Lens Flare / Optical Glow Burst ─────────────────────────────── */

const LensFlare: React.FC<{
	localFrame: number;
	color: string;
}> = ({localFrame, color}) => {
	const LIFE = 16;
	if (localFrame < 0 || localFrame > LIFE) return null;

	const t = localFrame;

	/* ---- Central radial glow (white-hot core → gold falloff) ---- */
	const coreScale = interpolate(t, [0, 3, LIFE], [0.25, 1.6, 2.8], {
		extrapolateRight: 'clamp',
	});
	const coreOpacity = interpolate(t, [0, 2, LIFE], [1, 0.6, 0], {
		extrapolateRight: 'clamp',
	});

	/* ---- Soft wide background spill ---- */
	const spillScale = interpolate(t, [0, 4, LIFE], [0.4, 2.2, 3.6], {
		extrapolateRight: 'clamp',
	});
	const spillOpacity = interpolate(t, [0, 2, LIFE], [0.55, 0.3, 0], {
		extrapolateRight: 'clamp',
	});

	/* ---- Horizontal anamorphic streak ---- */
	const hStreakX = interpolate(t, [0, 4, LIFE], [0.4, 4.5, 8], {
		extrapolateRight: 'clamp',
	});
	const hStreakOp = interpolate(t, [0, 2, 13], [0.92, 0.5, 0], {
		extrapolateRight: 'clamp',
	});

	/* ---- Vertical cross streak (thinner) ---- */
	const vStreakY = interpolate(t, [0, 3, LIFE], [0.3, 2.2, 3.8], {
		extrapolateRight: 'clamp',
	});
	const vStreakOp = interpolate(t, [0, 2, 11], [0.7, 0.3, 0], {
		extrapolateRight: 'clamp',
	});

	/* ---- Expanding ring / halo ---- */
	const ringScale = interpolate(t, [0, 5, LIFE], [0.4, 2.0, 3.2], {
		extrapolateRight: 'clamp',
	});
	const ringOp = interpolate(t, [0, 3, LIFE], [0.55, 0.2, 0], {
		extrapolateRight: 'clamp',
	});

	/* ---- Diagonal star rays (±45°) ---- */
	const rayLen = interpolate(t, [0, 4, LIFE], [0.2, 2.8, 4.5], {
		extrapolateRight: 'clamp',
	});
	const rayOp = interpolate(t, [0, 2, 12], [0.6, 0.25, 0], {
		extrapolateRight: 'clamp',
	});

	const abs: React.CSSProperties = {
		position: 'absolute',
		left: '50%',
		top: '50%',
		pointerEvents: 'none',
	};

	return (
		<div
			style={{
				position: 'absolute',
				inset: 0,
				overflow: 'visible',
				zIndex: 0,
				pointerEvents: 'none',
			}}
		>
			{/* Layer 1 — Soft background light spill */}
			<div
				style={{
					...abs,
					width: 520,
					height: 320,
					marginLeft: -260,
					marginTop: -160,
					borderRadius: '50%',
					background: `radial-gradient(ellipse, ${color}66 0%, ${color}22 40%, transparent 72%)`,
					transform: `scale(${spillScale})`,
					opacity: spillOpacity,
				}}
			/>

			{/* Layer 2 — Central white-hot core */}
			<div
				style={{
					...abs,
					width: 220,
					height: 220,
					marginLeft: -110,
					marginTop: -110,
					borderRadius: '50%',
					background: `radial-gradient(circle, rgba(255,255,255,0.97) 0%, rgba(255,255,240,0.85) 15%, ${color}cc 35%, ${color}55 55%, transparent 72%)`,
					transform: `scale(${coreScale})`,
					opacity: coreOpacity,
				}}
			/>

			{/* Layer 3 — Horizontal anamorphic streak */}
			<div
				style={{
					...abs,
					width: 420,
					height: 14,
					marginLeft: -210,
					marginTop: -7,
					borderRadius: 7,
					background: `linear-gradient(90deg, transparent 0%, ${color}55 12%, ${color}aa 30%, rgba(255,255,255,0.92) 50%, ${color}aa 70%, ${color}55 88%, transparent 100%)`,
					transform: `scaleX(${hStreakX}) scaleY(0.75)`,
					opacity: hStreakOp,
				}}
			/>

			{/* Layer 4 — Vertical cross streak */}
			<div
				style={{
					...abs,
					width: 6,
					height: 260,
					marginLeft: -3,
					marginTop: -130,
					borderRadius: 3,
					background: `linear-gradient(180deg, transparent 0%, ${color}33 15%, rgba(255,255,255,0.75) 50%, ${color}33 85%, transparent 100%)`,
					transform: `scaleY(${vStreakY}) scaleX(0.7)`,
					opacity: vStreakOp,
				}}
			/>

			{/* Layer 5 — Diagonal star rays (45° and -45°) */}
			{[45, -45].map((deg) => (
				<div
					key={deg}
					style={{
						...abs,
						width: 4,
						height: 200,
						marginLeft: -2,
						marginTop: -100,
						borderRadius: 2,
						background: `linear-gradient(180deg, transparent 0%, ${color}22 18%, rgba(255,255,255,0.55) 50%, ${color}22 82%, transparent 100%)`,
						transform: `rotate(${deg}deg) scaleY(${rayLen}) scaleX(0.6)`,
						opacity: rayOp,
					}}
				/>
			))}

			{/* Layer 6 — Expanding ring / halo */}
			<div
				style={{
					...abs,
					width: 280,
					height: 280,
					marginLeft: -140,
					marginTop: -140,
					borderRadius: '50%',
					background: 'transparent',
					boxShadow: `0 0 12px 3px ${color}88, inset 0 0 12px 3px ${color}44`,
					transform: `scale(${ringScale})`,
					opacity: ringOp,
				}}
			/>
		</div>
	);
};

/* ─── Foreground segment ──────────────────────────────────────────── */

const ForegroundSegment: React.FC<{
	segment: SubtitleSegment;
	segmentIndex: number;
	segmentStartFrame: number;
	nextSegmentStartFrame: number | null;
	frame: number;
	fps: number;
	fontSize: number;
	highlightFontSize: number;
	strokeWidth: number;
	highlightColor: string;
	normalStrokeColor: string;
	highlightStrokeColor: string;
	skewDeg: number;
	align?: 'center' | 'left' | 'right';
}> = ({
	segment,
	segmentIndex,
	segmentStartFrame,
	nextSegmentStartFrame,
	frame,
	fps,
	fontSize,
	highlightFontSize,
	strokeWidth,
	highlightColor,
	normalStrokeColor,
	highlightStrokeColor,
	skewDeg,
	align = 'center',
}) => {
	const t = frame - segmentStartFrame;
	if (t < 0) return null;

	const isHL = segment.highlight === true;
	const size = isHL ? highlightFontSize : fontSize;
	const fill = isHL ? highlightColor : '#FFFFFF';
	const sColor = isHL ? highlightStrokeColor : normalStrokeColor;
	const {webkitStroke, textShadow} = buildStrokeStyle(
		sColor,
		strokeWidth,
		isHL,
	);

	const isImpact = isHL && segment.impact === true;
	let scaleVal: number;
	let translateY: number;
	let brightness = 1;

	if (isImpact) {
		scaleVal = spring({
			frame: t,
			fps,
			from: 1.5,
			to: 1,
			config: {damping: 10, stiffness: 180, mass: 0.45},
		});
		translateY = interpolate(t, [0, 8], [-12, 0], {
			extrapolateRight: 'clamp',
			easing: Easing.out(Easing.cubic),
		});
		brightness = interpolate(t, [0, 3, 12], [1.45, 1.1, 1], {
			extrapolateRight: 'clamp',
		});
	} else if (isHL) {
		scaleVal = spring({
			frame: t,
			fps,
			from: 0.85,
			to: 1,
			config: {damping: 16, stiffness: 100, mass: 0.8},
		});
		translateY = interpolate(t, [0, 10], [12, 0], {
			extrapolateRight: 'clamp',
			easing: Easing.out(Easing.cubic),
		});
	} else {
		scaleVal = spring({
			frame: t,
			fps,
			from: 0.6,
			to: 1,
			config: {damping: 14, stiffness: 120, mass: 0.8},
		});
		translateY = interpolate(t, [0, 10], [18, 0], {
			extrapolateRight: 'clamp',
			easing: Easing.out(Easing.cubic),
		});
	}

	const enterOpacity = interpolate(t, [0, isHL ? 3 : 5], [0, 1], {
		extrapolateRight: 'clamp',
	});

	let fadeOut = 1;
	if (nextSegmentStartFrame !== null) {
		const FADE_FRAMES = 8;
		fadeOut = interpolate(
			frame,
			[nextSegmentStartFrame, nextSegmentStartFrame + FADE_FRAMES],
			[1, 0.35],
			{
				extrapolateLeft: 'clamp',
				extrapolateRight: 'clamp',
				easing: Easing.out(Easing.cubic),
			},
		);
	}

	const opacity = enterOpacity * fadeOut;

	const isStaggeredAlign = align === 'left' || align === 'right';
	const overlapMargin =
		segmentIndex > 0
			? isStaggeredAlign
				? Math.round(fontSize * 0.06)
				: Math.round(-fontSize * 0.38)
			: 0;

	const originMap = {
		left: 'left bottom',
		right: 'right bottom',
		center: 'center bottom',
	} as const;

	const effectiveSkew = isStaggeredAlign ? 0 : skewDeg;

	return (
		<div
			style={{
				position: 'relative',
				zIndex: segmentIndex,
				marginTop: overlapMargin,
				transform: `translateY(${translateY}px) scale(${scaleVal}) skewX(${effectiveSkew}deg)`,
				transformOrigin: originMap[align],
				opacity,
				filter: brightness !== 1 ? `brightness(${brightness})` : undefined,
				textAlign: align,
			}}
		>
			{isImpact && <LensFlare localFrame={t} color={highlightColor} />}
			<span
				style={{
					position: 'relative',
					zIndex: 1,
					display: 'inline-block',
					fontSize: size,
					fontWeight: 900,
					fontFamily: FONT,
					color: fill,
					WebkitTextStroke: webkitStroke,
					paintOrder: 'stroke fill',
					textShadow,
					lineHeight: 1.15,
					whiteSpace: 'nowrap',
					letterSpacing: '0.04em',
				}}
			>
				{segment.text}
			</span>
		</div>
	);
};

/* ─── Ghost (previous line pushed to background) ──────────────────── */

const GhostLine: React.FC<{
	line: SubtitleLine;
	transitionFrame: number;
	segmentInterval: number;
	frame: number;
	fontSize: number;
	highlightFontSize: number;
	strokeWidth: number;
	highlightColor: string;
	normalStrokeColor: string;
	highlightStrokeColor: string;
	skewDeg: number;
	layout: 'centered' | 'staggered';
}> = ({
	line,
	transitionFrame,
	segmentInterval,
	frame,
	fontSize,
	highlightFontSize,
	strokeWidth,
	highlightColor,
	normalStrokeColor,
	highlightStrokeColor,
	skewDeg,
	layout,
}) => {
	const elapsed = frame - transitionFrame;
	const fadeOutEnd = segmentInterval;

	const progress = interpolate(elapsed, [0, fadeOutEnd], [0, 1], {
		extrapolateLeft: 'clamp',
		extrapolateRight: 'clamp',
		easing: Easing.out(Easing.cubic),
	});

	const ghostScale = interpolate(progress, [0, 1], [1, 0.68]);
	const ghostOpacity = interpolate(progress, [0, 1], [0.85, 0]);
	const ghostY = interpolate(progress, [0, 1], [0, -6]);

	if (ghostOpacity <= 0) return null;

	const isStaggered = layout === 'staggered';

	return (
		<div
			style={{
				position: 'absolute',
				top: 0,
				left: 0,
				right: 0,
				bottom: 0,
				display: 'flex',
				flexDirection: 'column',
				alignItems: isStaggered ? 'stretch' : 'center',
				justifyContent: 'flex-end',
				zIndex: 0,
				transform: `translateY(${ghostY}px) scale(${ghostScale})`,
				opacity: ghostOpacity,
				pointerEvents: 'none',
			}}
		>
			{line.segments.map((seg, i) => {
				const isHL = seg.highlight === true;
				const size = isHL ? highlightFontSize : fontSize;
				const fill = isHL ? highlightColor : '#FFFFFF';
				const sColor = isHL ? highlightStrokeColor : normalStrokeColor;
				const {webkitStroke, textShadow} = buildStrokeStyle(
					sColor,
					strokeWidth,
					isHL,
				);
				const segAlign = isStaggered
					? isHL
						? 'right'
						: 'left'
					: 'center';
				const effectiveSkew = isStaggered ? 0 : skewDeg;

				return (
					<div
						key={i}
						style={{
							transform: `skewX(${effectiveSkew}deg)`,
							textAlign: segAlign as React.CSSProperties['textAlign'],
						}}
					>
						<span
							style={{
								display: 'inline-block',
								fontSize: size,
								fontWeight: 900,
								fontFamily: FONT,
								color: fill,
								WebkitTextStroke: webkitStroke,
								paintOrder: 'stroke fill',
								textShadow,
								lineHeight: 1.15,
								whiteSpace: 'nowrap',
								letterSpacing: '0.04em',
							}}
						>
							{seg.text}
						</span>
					</div>
				);
			})}
		</div>
	);
};

/* ─── Main component ──────────────────────────────────────────────── */

export const BoldStrokeSubtitle: React.FC<
	Partial<BoldStrokeSubtitleProps>
> = (props) => {
	const p = useMemo(() => ({...DEFAULT_PROPS, ...props}), [props]);
	const frame = useCurrentFrame();
	const {fps} = useVideoConfig();

	const {
		fontSize,
		highlightFontSize,
		strokeWidth,
		highlightColor,
		normalStrokeColor,
		highlightStrokeColor,
		segmentInterval,
		skewDeg,
		layout,
	} = p as Required<BoldStrokeSubtitleProps>;

	const positionStyle =
		p.position === 'center'
			? {top: 0, bottom: 0, justifyContent: 'center' as const}
			: {bottom: '12%'};

	const sortedLines = useMemo(
		() => [...p.lines].sort((a, b) => a.startFrame - b.startFrame),
		[p.lines],
	);

	let activeLine: SubtitleLine | null = null;
	let activeIdx = -1;

	for (let i = 0; i < sortedLines.length; i++) {
		const nextStart =
			i + 1 < sortedLines.length ? sortedLines[i + 1].startFrame : Infinity;
		if (frame >= sortedLines[i].startFrame && frame < nextStart) {
			activeLine = sortedLines[i];
			activeIdx = i;
			break;
		}
	}

	if (!activeLine) return null;

	const prevLine = activeIdx > 0 ? sortedLines[activeIdx - 1] : null;

	return (
		<AbsoluteFill style={{pointerEvents: 'none'}}>
			<div
				style={{
					position: 'absolute',
					left: 40,
					right: 40,
					...positionStyle,
				}}
			>
				<div style={{position: 'relative'}}>
					{prevLine && (
						<GhostLine
							line={prevLine}
							transitionFrame={activeLine.startFrame}
							segmentInterval={segmentInterval}
							frame={frame}
							fontSize={fontSize}
							highlightFontSize={highlightFontSize}
							strokeWidth={strokeWidth}
							highlightColor={highlightColor}
							normalStrokeColor={normalStrokeColor}
							highlightStrokeColor={highlightStrokeColor}
							skewDeg={skewDeg}
							layout={layout}
						/>
					)}

					<div
						style={{
							position: 'relative',
							zIndex: 1,
							display: 'flex',
							flexDirection: 'column',
							alignItems:
								layout === 'staggered' ? 'stretch' : 'center',
						}}
					>
						{activeLine.segments.map((seg, i) => {
							const segStart =
								activeLine!.startFrame + i * segmentInterval;
							const nextStart =
								i + 1 < activeLine!.segments.length
									? activeLine!.startFrame +
										(i + 1) * segmentInterval
									: null;
							const segAlign =
								layout === 'staggered'
									? seg.highlight
										? 'right'
										: 'left'
									: 'center';
							return (
								<ForegroundSegment
									key={`${activeLine!.startFrame}-${i}`}
									segment={seg}
									segmentIndex={i}
									segmentStartFrame={segStart}
									nextSegmentStartFrame={nextStart}
									frame={frame}
									fps={fps}
									fontSize={fontSize}
									highlightFontSize={highlightFontSize}
									strokeWidth={strokeWidth}
									highlightColor={highlightColor}
									normalStrokeColor={normalStrokeColor}
									highlightStrokeColor={highlightStrokeColor}
									skewDeg={skewDeg}
									align={segAlign as 'center' | 'left' | 'right'}
								/>
							);
						})}
					</div>
				</div>
			</div>
		</AbsoluteFill>
	);
};
